import { notify } from 'libs/common/notify'

import { getElopageConfig } from 'utils/elopageConfig.utils'
import { isProduction } from 'utils/env.utils'
import {
  loadStripe,
  type StripePaymentElementOptions,
  type PaymentIntentResult,
  type Stripe,
  type StripeElements,
  type SetupIntentResult,
  type StripeCardElement,
  type StripeCardNumberElement,
  type StripeElementLocale,
} from '@stripe/stripe-js'

import { StripeMessageParams } from 'shop/types/stripe'

import { PAYMENT_FORMS } from 'constants/paymentSettingShared.constants'
import { STRIPE_ELEMENTS_MODE } from 'constants/stripe.constants'

import { hasWindow } from './browser.utils'

type IntentResult = PaymentIntentResult | SetupIntentResult

/* App locales use underscores (pt_br, pt_pt) while Stripe Elements expects BCP-47
   tags — and Stripe has no pt-PT, only pt. Every other app locale is a valid
   StripeElementLocale as-is. */
export const getStripeLocale = (): StripeElementLocale => {
  switch (I18n.locale) {
    case 'pt_br':
      return 'pt-BR'
    case 'pt_pt':
      return 'pt'
    default:
      return I18n.locale as StripeElementLocale
  }
}

export const getStripeStyles = (paymentForm = '') => {
  switch (paymentForm) {
    case PAYMENT_FORMS.p24:
    case PAYMENT_FORMS.ideal:
    case PAYMENT_FORMS.klarna:
      return {
        base: {
          backgroundColor: '#fff',
          padding: '15px 13px',
          color: '#4a4a4a',
          lineHeight: '16px',
          fontFamily: 'Montserrat Reg, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '13px',
          '::placeholder': {
            color: '#4a4a4a',
          },
          ':hover': {
            backgroundColor: '#E4F0FA',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }

    case PAYMENT_FORMS.card:
    default:
      return {
        base: {
          color: '#32325d',
          lineHeight: '18px',
          fontFamily: 'Nunito Sans, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }
  }
}

export const getNewStripeStyles = (paymentForm = '') => {
  switch (paymentForm) {
    case PAYMENT_FORMS.p24:
    case PAYMENT_FORMS.ideal:
    case PAYMENT_FORMS.klarna:
      return {
        base: {
          backgroundColor: '#fff',
          padding: '16px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 400,

          ':hover': {
            backgroundColor: '#f3f5f8',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }

    case PAYMENT_FORMS.card:
    default:
      return {
        base: {
          color: '#000004',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          iconColor: '#485056',

          '::placeholder': {
            color: '#8f9295',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }
  }
}

export const isStripeInjected = () => hasWindow && window.Stripe

export const createStripeClient = async (stripePubKey = '') => await loadStripe(stripePubKey)

export const createStripeElements = (
  stripeClient?: Stripe,
  params?: { clientSecret: string } | StripeMessageParams
): object | void => {
  if (stripeClient) {
    return stripeClient.elements({
      locale: getStripeLocale(),
      ...(params ? params : {}),
    })
  }
}

export const createPaymentElement = (
  stripeElements: StripeElements,
  options: StripePaymentElementOptions
): object | void => {
  if (stripeElements) {
    return stripeElements.create('payment', {
      ...options,
    })
  }
}

export const createStripeP24 = (stripeElements?: {
  create: (provider: string, style: object) => object
}): object | void => {
  if (stripeElements) {
    return stripeElements.create('p24Bank', {
      style: getNewStripeStyles(PAYMENT_FORMS.p24),
    })
  }
}

export const createStripeCard = (stripeElements: StripeElements): object | void =>
  createPaymentElement(stripeElements, {
    fields: {
      billingDetails: 'never',
    },
  })

export const handleStripePaymentElementSetup = (
  stripeElement: StripeElements,
  stripeClient: Stripe,
  paymentMethodUserData: { redirectLink: string; id?: string; billingDetails?: object; confirmationToken?: string },
  setupIntentSecret?: string
): Promise<SetupIntentResult> => {
  if (paymentMethodUserData.id && setupIntentSecret) {
    return stripeClient.confirmSetup({
      clientSecret: setupIntentSecret,
      confirmParams: {
        payment_method: paymentMethodUserData.id,
        ...(paymentMethodUserData.redirectLink ? { return_url: paymentMethodUserData.redirectLink } : {}),
      },
      redirect: 'if_required',
    })
  }

  const confirmParams: Partial<Parameters<Stripe['confirmSetup']>[0]['confirmParams']> = {
    payment_method_data: {
      billing_details: paymentMethodUserData.billingDetails,
    },
    ...(paymentMethodUserData.redirectLink ? { return_url: paymentMethodUserData.redirectLink } : {}),
    confirmation_token: paymentMethodUserData.confirmationToken || '',
  }

  return stripeClient.confirmSetup({
    elements: stripeElement,
    clientSecret: setupIntentSecret,
    confirmParams,
    redirect: 'if_required',
  })
}

export const handleStripePaymentElementTransaction = (
  stripeElement: StripeElements,
  stripeClient: Stripe,
  paymentMethodUserData: { redirectLink: string; id?: string; billingDetails?: object; confirmationToken?: string },
  clientSecret?: string
): Promise<PaymentIntentResult> => {
  const confirmParams: Partial<Parameters<Stripe['confirmPayment']>[0]['confirmParams']> = {
    payment_method_data: {
      billing_details: paymentMethodUserData.billingDetails,
    },
    ...(paymentMethodUserData.redirectLink ? { return_url: paymentMethodUserData.redirectLink } : {}),
    confirmation_token: paymentMethodUserData.confirmationToken || '',
  }

  if (paymentMethodUserData.id && clientSecret) {
    return stripeClient.confirmPayment({
      clientSecret,
      confirmParams: {
        payment_method: paymentMethodUserData.id,
        ...(paymentMethodUserData.redirectLink ? { return_url: paymentMethodUserData.redirectLink } : {}),
      },
      redirect: 'if_required',
    })
  }

  return stripeClient.confirmPayment({
    elements: stripeElement,
    clientSecret,
    confirmParams,
    redirect: 'if_required',
  })
}

export const handleStripePaymentElementCard = (
  stripeElement: StripeElements,
  stripeClient: Stripe,
  paymentMethodUserData: { redirectLink: string; id?: string; billingDetails?: object; confirmationToken?: string },
  clientSecret?: string,
  setupIntentSecret?: string
) => {
  if (clientSecret) {
    return handleStripePaymentElementTransaction(stripeElement, stripeClient, paymentMethodUserData, clientSecret)
  }

  return handleStripePaymentElementSetup(stripeElement, stripeClient, paymentMethodUserData, setupIntentSecret)
}

export interface StripeBillingDetails {
  name?: string
  email?: string
  phone?: string
  address?: {
    line1?: string
    line2?: string
    city?: string
    state?: string
    postal_code?: string
    country?: string
  }
}

export const handleStripeCardTransaction = (
  stripeClient: Stripe,
  paymentMethodUserData: { id?: string; stripeCard?: StripeCardElement | StripeCardNumberElement },
  clientSecret: string,
  billingDetails?: StripeBillingDetails,
  setupIntentSecret?: string
): Promise<PaymentIntentResult> | Promise<SetupIntentResult> | undefined => {
  if (stripeClient && (clientSecret || setupIntentSecret)) {
    const paymentMethodData = paymentMethodUserData.id || {
      card: paymentMethodUserData.stripeCard,
      billing_details: billingDetails || {},
    }

    if (setupIntentSecret) {
      return stripeClient.confirmCardSetup(setupIntentSecret, {
        payment_method: paymentMethodData,
      })
    }

    return stripeClient.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodData,
    })
  }
}

interface HandleTransactionResponseOptions {
  /**
   * SCL-1894 R2-AC4 — suppress the provider's own wording as a toast, for a
   * caller that renders its own message in the page instead.
   *
   * Opt-in on purpose. This helper has thirteen external call sites across four
   * stores, a modal and a hook (orders.store x7, AutopayHandlerModal x2,
   * upgradeTickets.store x2, deals.store x1, usePlanCheckout x1) plus two
   * internal ones, and subscription and upgrade-ticket checkout rely on the
   * toast today; changing the default would silently take their only error
   * feedback away.
   */
  suppressNotify?: boolean
}

export const handleTransactionResponse = (
  result: IntentResult,
  handleSuccess: () => void,
  handleError: () => void,
  { suppressNotify = false }: HandleTransactionResponseOptions = {}
) => {
  const { error } = result

  if ((result as SetupIntentResult).setupIntent || (result as PaymentIntentResult).paymentIntent) {
    handleSuccess()
  } else if (error?.message) {
    if (!suppressNotify) {
      notify('error', error.message)
    }
    handleError()
  } else {
    handleError()
  }
}

export const handleAdditionalConfirmation = async (
  stripeClient: Stripe,
  handleSuccess: () => void,
  handleError: () => void,
  clientSecret: string,
  paymentMethodUserData?: { id?: string; stripeCard?: StripeCardElement | StripeCardNumberElement },
  billingDetails?: StripeBillingDetails,
  pubKey?: string
) => {
  const handleTransaction = async () => {
    const client = stripeClient || (await createStripeClient(pubKey))
    const resp = await client.retrievePaymentIntent(clientSecret)

    if (resp?.paymentIntent?.status?.includes('requires')) {
      handleStripeCardTransaction(client, paymentMethodUserData ?? {}, clientSecret, billingDetails).then(
        (result: IntentResult) => handleTransactionResponse(result, handleSuccess, handleError)
      )
    } else {
      handleSuccess()
    }
  }
  return await handleTransaction()
}

export const handleStripeCardRecuringPayment = (
  clientSecret: string,
  paymentMethod: string,
  stripePubKey: string,
  handleSuccess: () => void,
  handleError: () => void
): void => {
  const handleTransaction = async (): Promise<void> => {
    const stripeClient = await createStripeClient(stripePubKey)
    handleStripeCardTransaction(stripeClient, { id: paymentMethod }, clientSecret, {}).then((result: IntentResult) =>
      handleTransactionResponse(result, handleSuccess, handleError)
    )
  }
  handleTransaction()
}

export const getStripeElementsMode = (withSetupIntent: boolean) =>
  withSetupIntent ? STRIPE_ELEMENTS_MODE.setup : STRIPE_ELEMENTS_MODE.payment

export const convertPriceForStripe = (price: number): number => Math.round((Number(price) || 0) * 100)

export const allowedToUseStripe = () => {
  const webProtocol = getElopageConfig('webProtocol')
  const isHttp = webProtocol === 'http'
  return !(isProduction() && isHttp)
}
